import type { ValideurPaiements } from '../../services/api';
import { formatApiError } from '../../lib/api-error';
import { Spinner } from '../shared/Spinner';

/**
 * Sélecteur du valideur d'une demande de paiement ou de remboursement, le même
 * aux trois niveaux (tout, section, ligne). `value` vide = les lignes visées
 * n'ont pas toutes le même valideur : on l'affiche au lieu d'en choisir un.
 */
export function ChoixValideur({
  valideurs,
  value,
  onChange,
  label,
}: {
  valideurs: ValideurPaiements[];
  value: string;
  onChange: (email: string) => void;
  /** Ce que le choix vise, pour les lecteurs d'écran (« Valideur de la section … »). */
  label: string;
}) {
  return (
    <select
      aria-label={label}
      title={label}
      value={value}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-[#384872] focus:outline-none focus:ring-1 focus:ring-blue-500"
    >
      {value === '' && <option value="">Plusieurs valideurs</option>}
      {valideurs.map((v) => (
        <option key={v.email} value={v.email}>
          {v.nom}
        </option>
      ))}
    </select>
  );
}

/**
 * L'état de la liste des valideurs, quand elle n'est pas là.
 *
 * Sans elle, `ChoixValideur` disparaît et la colonne « Valideur » se vide : les
 * deux écrans de préparation (« À payer », « À rembourser ») ressemblent alors à
 * une application sans valideur réglé, alors que c'est une panne de chargement.
 * Pire, on peut soumettre dans cet état — les lignes partent à leur valideur
 * actuel ou au défaut, sans que personne ne l'ait choisi. Le dire est donc la
 * seule façon de ne pas soumettre à l'aveugle.
 *
 * Composant partagé par les deux écrans : la phrase ne doit exister qu'une
 * fois, sinon un correctif ne touche qu'une des deux copies.
 */
export function EtatListeValideurs({
  erreur,
  chargement,
  relanceEnCours,
  onReessayer,
  objet,
}: {
  erreur: unknown;
  chargement: boolean;
  relanceEnCours: boolean;
  onReessayer: () => void;
  /** Ce que l'écran soumet, au singulier, tel qu'il se lit dans « chaque … » : « facture », « avoir ». */
  objet: string;
}) {
  if (erreur) {
    return (
      <div className="rounded-md bg-red-50 text-[#C0423F] px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span>
          ⚠ Liste des valideurs non chargée : {formatApiError(erreur)}. Le choix du valideur n'est
          pas proposé ; soumettre maintenant enverrait chaque {objet} à son valideur actuel, ou au
          valideur par défaut.
        </span>
        <button
          type="button"
          onClick={onReessayer}
          disabled={relanceEnCours}
          className="px-2.5 py-1 rounded-md text-xs font-medium text-[#C0423F] ring-1 ring-[#C0423F]/40 hover:bg-white disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {relanceEnCours && <Spinner />}
          Réessayer
        </button>
      </div>
    );
  }
  if (chargement) {
    return <p className="text-sm text-slate-500">Chargement de la liste des valideurs…</p>;
  }
  return null;
}

/** Le valideur commun à des lignes, ou '' quand elles diffèrent. */
export function valideurCommun(emails: string[]): string {
  const uniques = [...new Set(emails)];
  return uniques.length === 1 ? uniques[0]! : '';
}

/** « 3 à Dominique, 2 à Fabien » — pour le bouton et le message de soumission. */
export function repartitionParValideur(par: Array<{ nom: string; lignes: number }>): string {
  return par.map((g) => `${g.lignes} à ${g.nom}`).join(', ');
}
